if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );
          
          changeSeason(this.currentSeason);

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId, bg, layers) {
        var params = new URLSearchParams
        if (variantId) params.append('variant',variantId)
        if (bg) params.append('bg',bg)
        if (layers) {
          if (layers.isArray()) layers.forEach((value, index) => params.append(index,value))
          if (!layers.isArray()) params.append('1',layers)
        }
        
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${params.size > 0 ? `?${params.toString()}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        
        window.history.replaceState({}, '', `${url}${params.size > 0 ? `?${params.toString()}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;
        if(true) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      get currentSeason() {
        return this.querySelector('fieldset[data-option-name="Colour"] span[data-selected-value]').innerText
      }
    }
  );
}

var dynasheet, bespokeURL

if (!customElements.get('ck-options')) {
  customElements.define(
    'ck-options',
    class CkOptions extends HTMLElement {
      constructor() {
        super();
        this.querySelectorAll('fieldset').forEach((el) => {
          this.fieldsetChange(el)
        })
      }
      
      fieldsetChange(el){
        el.addEventListener('change',this.updateAll.bind(this));
      }
      
      updateAll(){
        // update each text field
        this.querySelector('ck-input-seasonset').updateSelectedText()
        this.querySelectorAll('ck-input-swatchset').forEach((el) => {
          el.updateSelectedText()
        })
        
        // update image
        var selected_image_url, selected_image_url_thumb
        
        if(dynasheet == null) this.querySelector('ck-input-seasonset').initiateDynasheet()
        if(['Autumn','Winter','Spring','Summer'].includes(this.selectedSeason)) {
          if(this.querySelector('ck-input-seasonset').selectedSeason){
            dynasheet.rules[0].style.backgroundImage = 'url(' + this.querySelector('ck-input-seasonset').selectedVariantObj.image_url + ')';
            dynasheet.rules[1].style.backgroundImage = 'url(' + this.querySelector('ck-input-seasonset').selectedVariantObj.image_url_thumb + ')';
          }
          if(document.querySelector('ck-input-swatchset[data-layer="bg"]')){
          document.querySelector('ck-input-swatchset[data-layer="bg"] .swatch-input').style = '--swatch--background:'+ this.querySelector('ck-input-seasonset').selectedVariantObj.default_background;
          document.querySelector('ck-input-swatchset[data-layer="bg"] input#default-bg').value = this.querySelector('ck-input-seasonset').selectedVariantObj.default_background_name;
          document.querySelector('ck-input-swatchset[data-layer="bg"] input#default-bg').parentElement.dataset.colourName = this.querySelector('ck-input-seasonset').selectedVariantObj.default_background_name;
          if (document.querySelector('ck-input-swatchset[data-layer="bg"] input#default-bg').checked){
            document.querySelector('ck-input-swatchset[data-layer="bg"]').selectedValue.innerText = this.querySelector('ck-input-seasonset').selectedVariantObj.default_background_name;
            document.querySelector(`ck-input-swatchset[data-layer="bg"] input[value="${this.querySelector('ck-input-seasonset').selectedVariantObj.default_background_name}"]`).checked = true;
          }
        }
        } else {
          this.querySelector('ck-input-seasonset').bespokeImage()
        }
        
        this.querySelectorAll('ck-input-swatchset').forEach((el) => {
          if(el.dataset.layer == 'bg'){
            var colour = el.querySelector('input:checked').value
            if(dynasheet == null) el.initiateDynasheet()
            if(/^#[0-9A-F]{6}$/i.test(colour) || /^[0-9A-F]{6}$/i.test(colour)) {
              dynasheet.rules[0].style.backgroundColor = colour;
            } else {
              var chex = colour_library[colour]
              dynasheet.rules[0].style.backgroundColor = chex;
            }
          } else {
            // WIP change layer this.dataset.layer
            console.log('not written yet, can only change BG layer')
          }
        })

        //update prices
        this.priceDisplays.forEach((el) => {el.innerText = this.nowPrice})
        this.wasPriceDisplays.forEach((el) => {el.innerText = this.wasPrice})

        //update ID
        this.variantIdInput.value = this.selectedVariantId;
        
        //write/update attributes
        this.querySelector('ck-input-swatchset').updateAll()
        if (this.isBespoke) this.querySelector('ck-input-seasonset').updateAll()
      }

      get nowPrice(){
        return this.isBespoke ? season_variant["Bespoke"].variant_prices[this.selectedMaterial] : season_variant[this.selectedSeason].variant_prices[this.selectedMaterial]
      }

      get wasPrice(){
        return this.isBespoke ? season_variant["Bespoke"].variant_compare_at_prices[this.selectedMaterial] : season_variant[this.selectedSeason].variant_compare_at_prices[this.selectedMaterial]
      }

      get priceDisplays() {
        return this.closest('section').querySelectorAll('.price-item--regular')
      }

      get wasPriceDisplays() {
        return this.closest('section').querySelectorAll('.price-item--sale')
      }

      get selectedSeason () {
        return this.querySelector('ck-input-seasonset input:checked').value
      }

      get selectedMaterial () {
        return this.querySelector('fieldset[data-field="Wallpaper Type"] input:checked').value
      }
      
      get selectedVariantId () {
        return this.isBespoke ? season_variant["Bespoke"].variant_ids[this.selectedMaterial] : season_variant[this.selectedSeason].variant_ids[this.selectedMaterial]
      }

      get isBespoke(){
        return ['Autumn','Winter','Spring','Summer'].includes(this.selectedSeason) ? false : true
      }
      
      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
      
      get variantIdInput () {
        return document.querySelector('product-form[data-section-id="'+this.sectionId+'"] input[name="id"]')
      }
  })
}

if (!customElements.get('ck-input-seasonset')) {
  customElements.define(
    'ck-input-seasonset',
    class CkInputSeasonset extends HTMLElement {
      constructor() {
        super();
        if(this.swatchAddButton) this.swatchAddButton.addEventListener('click', this.onAddButtonClick.bind(this));
      }
      
      handleChange() {

      }

      writeNewSwatch(colour) {
        if(this.querySelector(`ck-input-swatchset .swatch-input[data-colour-name^="${colour}"]`)) {
          this.querySelector(`ck-input-swatchset .swatch-input input[value^="${colour}"]`).checked = true;
        } else {
          this.fieldset.children[this.fieldset.children.length - 2].insertAdjacentHTML('afterend', `<div class="swatch-input" data-colour-name="${colour}" style="--swatch--background:${colour_library[colour]}">
                <input name="season" id="custom-season-${this.swatches.length}" class="swatch-input__input" type="radio" value="${colour}" checked>
                <label for="custom-season-${this.swatches.length}" class="swatch-input__label" title="${colour}" >
                  <span class="swatch"></span>
                </label>
                <ck-remove data-colour-name="${colour}"><svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-close" viewBox="0 0 18 17"><path fill="currentColor" d="M.865 15.978a.5.5 0 0 0 .707.707l7.433-7.431 7.579 7.282a.501.501 0 0 0 .846-.37.5.5 0 0 0-.153-.351L9.712 8.546l7.417-7.416a.5.5 0 1 0-.707-.708L8.991 7.853 1.413.573a.5.5 0 1 0-.693.72l7.563 7.268z"></path></svg></ck-remove>
              </div>`);
        }

        // dispatch Change event to trigger listeners
        var evt = new Event('change');
        this.fieldset.dispatchEvent(evt);
      }
  
      removeSwatch(colour){
        var el = this.querySelector(`.swatch-input[data-colour-name^="${colour}"]`)
        if(el.querySelector('input').checked){
          this.defaultInput.querySelector('input').checked = true;
          this.updateAll();
        }
        el.parentElement.removeChild(el)

        // dispatch Change event to trigger listeners
        var evt = new Event('change');
        this.fieldset.dispatchEvent(evt);
      }

      updateImageLayer(){
        
        
      }
      
      updateImages(){
        if(dynasheet == null) this.initiateDynasheet()
        if(this.selectedSeason){
          dynasheet.rules[0].style.backgroundImage = 'url(' + this.selectedVariantObj.image_url + ')';
          dynasheet.rules[1].style.backgroundImage = 'url(' + this.selectedVariantObj.image_url_thumb + ')';
        }
        if(document.querySelector('ck-input-swatchset[data-layer="bg"]')){
          document.querySelector('ck-input-swatchset[data-layer="bg"] .swatch-input').style = '--swatch--background:'+ this.selectedVariantObj.default_background;
          document.querySelector('ck-input-swatchset[data-layer="bg"] input#default-bg').value = this.selectedVariantObj.default_background_name;
          document.querySelector('ck-input-swatchset[data-layer="bg"] input#default-bg').parentElement.dataset.colourName = this.selectedVariantObj.default_background_name;
          if (document.querySelector('ck-input-swatchset[data-layer="bg"] input#default-bg').checked){
            document.querySelector('ck-input-swatchset[data-layer="bg"]').selectedValue.innerText = this.selectedVariantObj.default_background_name;
            document.querySelector(`ck-input-swatchset[data-layer="bg"] input[value="${this.selectedVariantObj.default_background_name}"]`).checked = true;
          }
        }
        // update corresponding input
      }

      updateURL(bg, layers) {
        const url = this.closest('product-info').dataset.url;
        var variantId = this.selectedVariantObj.variant_ids[this.selectedSeason] || null; // needs updating when multiple product options for paper type
        var bg = document.querySelector('ck-input-swatchset[data-layer="bg"] input:checked').value;
        var params = new URLSearchParams
        if (variantId) params.append('variant',variantId)
        if (bg) params.append('bg',bg)
        if (layers) {
          if (layers.isArray()) layers.forEach((value, index) => params.append(index,value))
          if (!layers.isArray()) params.append('1',layers)
        }
        
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${params.size > 0 ? `?${params.toString()}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        
        window.history.replaceState({}, '', `${url}${params.size > 0 ? `?${params.toString()}` : ''}`);
      }

      updateSelectedText(){
        this.querySelector('span[data-selected-value]').innerText = this.querySelector('input:checked').value
      }

      updateProperties(){
        this.closest('section').querySelector('input.colour-'+this.linkedLibrary.dataset.layer).value = this.isBespoke ? this.selectedValue.innerText : ""
        document.querySelector('input.sample-colour-'+this.linkedLibrary.dataset.layer).value = this.selectedValue.innerText
      }

      updateAll(){
        this.updateSelectedText()
        this.updateImageLayer()
        this.updateProperties()
      }
      
      initiateDynasheet(){
        for(var s=0;s<document.styleSheets.length;s++){
          if(document.styleSheets[s].ownerNode.id == "dynamic-styling"){
            dynasheet = document.styleSheets[s];
          }
        }
      }

      onAddButtonClick(){
        this.linkedLibrary.show()
      }

      bespokeImage(){
        if(['Autumn','Winter','Spring','Summer'].includes(this.selectedValue)) return
        var hiddencanvas = document.createElement('canvas');
        var image = document.getElementById('bespoke-image-'+this.dataset.layer);
        hiddencanvas.width = image.naturalWidth;
        hiddencanvas.height = image.naturalHeight;
        var ctx = hiddencanvas.getContext('2d');
        ctx.drawImage(image,0,0,hiddencanvas.width,hiddencanvas.height)
        const original = ctx.getImageData(0, 0, hiddencanvas.width, hiddencanvas.height).data
        var mod = new Uint8ClampedArray(original.length)
        var new_rgb = hex(colour_library[this.selectedSeason]), init_rgb = {r: 255, g: 255, b:255};
        for (let i = 0; i < mod.length; i += 4) {
            var r = original[i]
            , g = original[i+1]
            , b = original[i+2]
            mod[i] = Math.max(Math.min(255,((r - init_rgb.r) + new_rgb.r)),0)
            mod[i+1] = Math.max(Math.min(255,((g - init_rgb.g) + new_rgb.g)),0)
            mod[i+2] = Math.max(Math.min(255,((b - init_rgb.b) + new_rgb.b)),0)
            mod[i+3] = original[i+3];
        }

        ctx.putImageData(new ImageData(mod,hiddencanvas.width,hiddencanvas.height),0,0)
        
        hiddencanvas.toBlob((blob) => {
          // upload_file = new File([blob], "img.png",{type:"image/png", lastModified:new Date().getTime()});

          setTimeout(() => {
            URL.revokeObjectURL(bespokeURL)
          },500)
          bespokeURL = URL.createObjectURL(blob);

          dynasheet.rules[0].style.backgroundImage = 'url(' + bespokeURL + ')';
          dynasheet.rules[1].style.backgroundImage = 'url(' + bespokeURL + ')';
          
          try{document.getElementById('test').src = bespokeURL }catch(e){}
          
        });
      }
      
      get selectedSeason(){
        return this.querySelector('input:checked').value
      }

      get isBespoke(){
        return ['Autumn','Winter','Spring','Summer'].includes(this.selectedSeason) ? false : true
      }
      
      get selectedVariantObj(){
        return season_variant[this.selectedSeason] 
      }

      get swatchAddButton(){
        return this.querySelector('.swatch-input.custom')
      }

      get linkedLibrary(){
        return document.querySelector(`ck-colour-picker[data-layer="${this.dataset.layer}"]`)
      }

      get fieldset() {
        return this.querySelector('fieldset')
      }

      get swatches(){
        return this.querySelectorAll('.swatch-input:not(.custom)')
      }

      get selectedValue() {
        return this.querySelector('span[data-selected-value]')
      }

      get defaultInput(){
        return this.querySelector('.swatch-input')
      }
    })
  }


if (!customElements.get('ck-input-swatchset')) {
  customElements.define(
    'ck-input-swatchset',
    class CkInputSwatchset extends HTMLElement {

      constructor() {
        super();
        this.swatchAddButton.addEventListener('click', this.onAddButtonClick.bind(this));
      }

      writeNewSwatch(colour) {
        if(this.querySelector(`ck-input-swatchset .swatch-input[data-colour-name^="${colour}"]`)) {
          this.querySelector(`ck-input-swatchset .swatch-input input[value^="${colour}"]`).checked = true;
        } else {
          this.fieldset.children[this.fieldset.children.length - 2].insertAdjacentHTML('afterend', `<div class="swatch-input" data-colour-name="${colour}" style="--swatch--background:${colour_library[colour]}">
                <input name="colour-bg" id="custom-bg-${this.swatches.length}" class="swatch-input__input" type="radio" value="${colour}" checked>
                <label for="custom-bg-${this.swatches.length}" class="swatch-input__label" title="${colour}" >
                  <span class="swatch"></span>
                </label>
                <ck-remove data-colour-name="${colour}"><svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-close" viewBox="0 0 18 17"><path fill="currentColor" d="M.865 15.978a.5.5 0 0 0 .707.707l7.433-7.431 7.579 7.282a.501.501 0 0 0 .846-.37.5.5 0 0 0-.153-.351L9.712 8.546l7.417-7.416a.5.5 0 1 0-.707-.708L8.991 7.853 1.413.573a.5.5 0 1 0-.693.72l7.563 7.268z"></path></svg></ck-remove>
              </div>`);
        }

        // dispatch Change event to trigger listeners
        var evt = new Event('change');
        this.fieldset.dispatchEvent(evt);
      }
  
      removeSwatch(colour){
        var el = this.querySelector(`.swatch-input[data-colour-name^="${colour}"]`)
        if(el.querySelector('input').checked){
          this.defaultInput.querySelector('input').checked = true;
          this.updateAll();
        }
        el.parentElement.removeChild(el)
      }

      updateAll(){
        this.updateSelectedText()
        this.updateImageLayer()
        this.updateProperties()
      }

      updateImageLayer(){
        if(this.dataset.layer == 'bg'){
          var colour = this.querySelector('input:checked').value

          if(dynasheet == null) this.initiateDynasheet()
          
          if(/^#[0-9A-F]{6}$/i.test(colour) || /^[0-9A-F]{6}$/i.test(colour)) {
            dynasheet.rules[0].style.backgroundColor = colour;
          } else {
            var hex = colour_library[colour]
            dynasheet.rules[0].style.backgroundColor = hex;
          }
        } else {
          // WIP change layer this.dataset.layer
          console.log('not written yet, can only change BG layer')
        }
      }

      updateSelectedText(){
        this.querySelector('span[data-selected-value]').innerText = this.querySelector('input:checked').value
      }

      updateProperties(){
        this.closest('section').querySelector('input.colour-'+this.linkedLibrary.dataset.layer).value = this.selectedValue.innerText
        document.querySelector('input.sample-colour-'+this.linkedLibrary.dataset.layer).value = this.selectedValue.innerText
      }

      initiateDynasheet(){
        for(var s=0;s<document.styleSheets.length;s++){
          if(document.styleSheets[s].ownerNode.id == "dynamic-styling"){
            dynasheet = document.styleSheets[s];
          }
        }
      }

      onAddButtonClick(){
        this.linkedLibrary.show()
      }

      get checkedValue(){
        return this.querySelector('input:checked').value
      }

      get linkedLibrary(){
        return document.querySelector(`ck-colour-picker[data-layer="${this.dataset.layer}"]`)
      }

      get swatches(){
        return this.querySelectorAll('.swatch-input:not(.custom)')
      }

      get swatchAddButton(){
        return this.querySelector('.swatch-input.custom')
      }

      get defaultInput(){
        return this.querySelector('.swatch-input')
      }

      get fieldset() {
        return this.querySelector('fieldset')
      }

      get selectedValue() {
        return this.querySelector('span[data-selected-value]')
      }
    }
  );
}

function hex(hex) {
  var rgb = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return rgb ? {
    r: parseInt(rgb[1], 16),
    g: parseInt(rgb[2], 16),
    b: parseInt(rgb[3], 16)
  } : null;
}

if (!customElements.get('ck-colour-picker')) {
  customElements.define(
    'ck-colour-picker',
    class CkColourPicker extends HTMLElement {
      constructor() {
        super();
        this.closeButton.addEventListener('click', this.hide.bind(this));
      }
      show() {
        this.classList.remove('hidden','fade')
        this.classList.remove('fade')
      }
      hide() {
        this.classList.add('fade')
        setTimeout(this.classList.add('hidden'),500)
      }

      get closeButton() {
        return this.querySelector('button[aria-label="Close"]')
      }

      get linkedSwatchset() {
        return document.querySelector(`ck-input-swatchset[data-layer="${this.dataset.layer}"]`) || document.querySelector(`ck-input-seasonset[data-layer="${this.dataset.layer}"]`)
      }
    }
  );
}

if (!customElements.get('ck-colour')) {
  customElements.define(
    'ck-colour',
    class CkColour extends HTMLElement {
      constructor() {
        super();
        this.addEventListener('click', this.onSwatchClick.bind(this));
      }

      onSwatchClick() {
        this.linkedLibrary.hide()
        this.linkedLibrary.linkedSwatchset.writeNewSwatch(this.dataset.colourName)
        this.linkedSwatchset.updateProperties()
      }

      get linkedLibrary() {
        return this.closest('ck-colour-picker')
      }

      get linkedSwatchset() {
        return this.linkedLibrary.linkedSwatchset
      }

      get linkedForm() {
        return this.linkedLibrary.linkedSwatchset.closest('section').querySelector('product-form')
      }
    }
  );
}

if (!customElements.get('ck-remove')) {
  customElements.define(
    'ck-remove',
    class CkRemove extends HTMLElement {
      constructor() {
        super();
        this.addEventListener('click',this.onClick.bind(this))
      }

      onClick() {
        this.linkedSwatchset.removeSwatch(this.dataset.colourName)
      }

      get linkedSwatchset() {
        return this.closest('ck-input-swatchset') || this.closest('ck-input-seasonset')
      }
    }
  )
}